import React, { useEffect } from 'react';
import App from './pages/App.tsx';
import './index.scss';
import ClientDashboard from "./modules/dashboard/ClientDashboard";
import Loader from './components/common/loader/loader.tsx';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import Login from './pages/authentication/login.tsx';
import Auth from './firebase/auth.tsx';
import { Paths } from './paths.tsx';
import SequenceReviewPage from './modules/sequenceReview/SequenceReviewPage';
import "./shared/styles/styles.scss";

import { setNavigator } from "./navigate";
import EngagementList from './modules/engagement/EngagementsPage.tsx';
import EngagementDetailPage from './modules/engagement/EngagementDetailPage.tsx';
import Organizations from './modules/organizations/organizations.tsx';
import OrganizationDetail from './modules/organizations/organizationDetail.tsx';
import FontUploader from './modules/fontUploader/fontUploader.tsx';

import JobsView from './modules/jobs/JobsView.tsx';
import JobDetail from './modules/jobs/JobsDetail.tsx';
import { NetworkTemplatesPage } from './modules/contentNetwork/networkTemplates/NetworkTemplatesPage.tsx';
import ChangePassword from './modules/change-password/ChangePassword.tsx';
import RequestChange from './modules/change-password/RequestChange.tsx';
import ChangePasswordExternal from './modules/change-password/ChangePasswordExternal.tsx';
import TracesPage from "./modules/traces/TracesPage.tsx";
import OpsDashboardPage from './modules/opsDashboard/OpsDashboardPage';
import IngestListPage from "./modules/ingest/IngestListPage.tsx";
import IngestFilesPage from "./modules/ingest/IngestFilesPage.tsx";
import QuarantinePage from "./modules/ingest/QuarantinePage.tsx";
import DataIssuesPage from "./modules/dataIssues/DataIssuesPage";
import DiscrepancyPage from "./modules/dataIssues/DiscrepancyPage";
import FieldMapEditorPage from "./modules/ingest/FieldMapEditorPage.tsx";
import InstructionsPage from "./modules/ingest/InstructionsPage.tsx";
import CatalogBrowserPage from "./modules/catalog/CatalogBrowserPage.tsx";
import EmailSmsCatalogPage from "./modules/emailSmsCatalog/EmailSmsCatalogPage.tsx";
import UploadsListPage from "./modules/uploads/UploadsListPage.tsx";
import UploadPage from "./modules/uploads/UploadPage.tsx";
import AccountSetupPage from "./modules/accountSetup/AccountSetupPage.tsx";
import ContentCatalogPage from './modules/contentCatalog/ContentCatalogPage.tsx';
import HoldoutReportPage from './modules/holdout/HoldoutReportPage.tsx';
import PermissionsPage from './modules/permissions/PermissionsPage.tsx';
import JourneyStepManagerPage from './modules/journeyStepManager/JourneyStepManagerPage.tsx';
import AdEditor from './modules/adEditor/AdEditor.tsx';
import { ProjectListPage } from './modules/createStudio/ProjectListPage';
import StandaloneEditor from './modules/createStudio/StandaloneEditor';
import AdCardDemoPage from '@/modules/devtools/AdCardDemoPage';
import MyTodosPage from './modules/myTodos/MyTodosPage';
import ReviewPanelPage from './modules/myTodos/ReviewPanelPage';
import MotiStorePage from './modules/motiStore/MotiStorePage';
import CollectionStorePage from './modules/motiStore/CollectionStorePage';
import OldClientDashboard from './modules/oldDashboard/OldClientDashboard';
import OpLaunchListPage from './modules/opLaunchPages/list/OpLaunchListPage';
import OpLaunchDetailPage from './modules/opLaunchPages/detail/OpLaunchDetailPage';
import OpLaunchDashboardPage from './modules/opLaunchPages/dashboard/OpLaunchDashboardPage';
import BannersPage from './modules/banners/BannersPage';
import BannerEditorPage from './modules/banners/BannerEditorPage';
import OpReportsListPage from './modules/opReports/OpReportsListPage';
import OpReportPage from './modules/opReports/OpReportPage';
import { MARKETING_BANNERS, OP_LAUNCH_DASHBOARD, OP_LAUNCH_LIST, OP_REPORTS, useFeatureFlag } from './shared/featureFlags/useFeatureFlag';
import NotebookPopout from './modules/notebook/NotebookPopout.tsx';
import NoAccessPage from './modules/noAccess/NoAccessPage.tsx';
import ContractDetailPage from './modules/contracts/pages/ContractDetailPage.tsx';
import ContractPricesheetPage from './modules/contracts/pages/ContractPricesheetPage.tsx';


/*
  Router Component created so we can navigate to a Route from anywhere in the App.
  This component is used below in the AppRouter.
*/
function RouterWithNavigation() {
  const navigate = useNavigate();
  // Flag-gated staff pages: off means the route does not exist, not that it renders empty.
  const opLaunchListOn = useFeatureFlag(OP_LAUNCH_LIST);
  const opLaunchDashboardOn = useFeatureFlag(OP_LAUNCH_DASHBOARD);
  const bannersOn = useFeatureFlag(MARKETING_BANNERS);
  const opReportsOn = useFeatureFlag(OP_REPORTS);

  useEffect(() => {
    setNavigator(navigate); // set the React Dom navigator so we can route from any function in the app.
  }, [navigate]);

  return (
    <Routes>
      <Route path={`${import.meta.env.BASE_URL}`} element={<Auth />}>
        <Route index element={<Login />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CHANGE_PASSWORD_EXTERNAL}`} element={<ChangePasswordExternal />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.REQUEST_CHANGE_PASSWORD}`} element={<RequestChange />} />
      </Route>
      <Route path={`${import.meta.env.BASE_URL}`} element={<App hasSidebar />}>
        <Route path={`${import.meta.env.BASE_URL}${Paths.HOME}`} element={<ClientDashboard />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}`} element={<EngagementList />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CONFIG}`} element={<IngestListPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.JOBS}`} element={<JobsView />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CONTENT_CATALOG}`} element={<ContentCatalogPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ORGANIZATIONS}`} element={<Organizations />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ORGANIZATION_DETAIL}/:organizationId`} element={<OrganizationDetail />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.JOBS}/:jobId`} element={<JobDetail />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.NETWORK_TEMPLATES}`} element={<NetworkTemplatesPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.TRACER}`} element={<TracesPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.OPS_DASHBOARD}`} element={<OpsDashboardPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.PERMISSIONS}`} element={<PermissionsPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.HOLDOUT_REPORT}`} element={<HoldoutReportPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.JOURNEY_STEPS}`} element={<JourneyStepManagerPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CATALOG}`} element={<CatalogBrowserPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.EMAIL_SMS_CATALOG}`} element={<EmailSmsCatalogPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CHANGE_PASSWORD}`} element={<ChangePassword />} />
        <Route path={`${import.meta.env.BASE_URL}devtools/adcard`} element={<AdCardDemoPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CREATE}`} element={<ProjectListPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.MY_TODOS}`} element={<MyTodosPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.MOTI_STORE}`} element={<MotiStorePage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.MOTI_STORE}/collection/:collectionId`} element={<CollectionStorePage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.MOTI_STORE}/tag/:tagId`} element={<CollectionStorePage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CLIENT_DASHBOARD_LEGACY}`} element={<OldClientDashboard />} />
        {opLaunchListOn && <Route path={`${import.meta.env.BASE_URL}${Paths.OP_LAUNCHES}`} element={<OpLaunchListPage />} />}
        {opLaunchListOn && <Route path={`${import.meta.env.BASE_URL}${Paths.OP_LAUNCH_DETAIL}`} element={<OpLaunchDetailPage />} />}
        {opLaunchDashboardOn && <Route path={`${import.meta.env.BASE_URL}${Paths.OP_LAUNCH_DASHBOARD}`} element={<OpLaunchDashboardPage />} />}
        {bannersOn && <Route path={`${import.meta.env.BASE_URL}${Paths.BANNERS}`} element={<BannersPage />} />}
        {bannersOn && <Route path={`${import.meta.env.BASE_URL}${Paths.BANNER_NEW}`} element={<BannerEditorPage />} />}
        {bannersOn && <Route path={`${import.meta.env.BASE_URL}${Paths.BANNER_EDIT}`} element={<BannerEditorPage />} />}
        {opReportsOn && <Route path={`${import.meta.env.BASE_URL}${Paths.OP_REPORTS}`} element={<OpReportsListPage />} />}
        {opReportsOn && <Route path={`${import.meta.env.BASE_URL}${Paths.OP_REPORT}`} element={<OpReportPage />} />}
        {/*<Route path={`${import.meta.env.BASE_URL}/ingest`} element={<IngestListPage />} />*/}

        <Route
          path={`${import.meta.env.BASE_URL}${Paths.INGEST_FILES}`} element={<IngestFilesPage />}
        />
        <Route
          path={`${import.meta.env.BASE_URL}${Paths.QUARANTINE}`} element={<QuarantinePage />}
        />
        <Route path={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}`} element={<DataIssuesPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/report`} element={<DataIssuesPage report />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/:discrepancyId`} element={<DiscrepancyPage />} />
        <Route
          path={`${import.meta.env.BASE_URL}${Paths.FIELD_MAP}`} element={<FieldMapEditorPage />}
        />
        <Route
          path={`${import.meta.env.BASE_URL}${Paths.INSTRUCTIONS}`} element={<InstructionsPage />}
        />
        <Route path={`${import.meta.env.BASE_URL}${Paths.UPLOADS}`} element={<UploadsListPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.UPLOAD}`} element={<UploadPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ACCOUNT_SETUP}`} element={<AccountSetupPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.NO_ACCESS}`} element={<NoAccessPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CONTRACT_DETAIL}/:contractId`} element={<ContractDetailPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CONTRACT_PRICESHEET}`} element={<ContractPricesheetPage />} />

      </Route>
      {/* Paths without header or sidebar */}
      <Route path={`${import.meta.env.BASE_URL}`} element={<App noChrome />}>
        <Route path={`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/:engagementId`} element={<EngagementDetailPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/:engagementId/:tab`} element={<EngagementDetailPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/:engagementId/:tab/:itemId`} element={<EngagementDetailPage />} />
        {/* Fourth segment: Monitor drills into two kinds of record, so it types
            them in the path — /monitor/objective/:id. See useMonitorRoute. */}
        <Route path={`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/:engagementId/:tab/:itemId/:subId`} element={<EngagementDetailPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.NOTEBOOK_POPOUT}/:notebookId`} element={<NotebookPopout />} />
        <Route path={`${import.meta.env.BASE_URL}editor-playground`} element={<Navigate to={`/${Paths.CREATE}`} replace />} />
        <Route path={`${import.meta.env.BASE_URL}kinema/ads/:adId`} element={<AdEditor />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.CREATE}/projects/:layeredMediaId`} element={<StandaloneEditor />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.MY_TODOS_REVIEW}/:actionId`} element={<ReviewPanelPage />} />
        <Route path={`${import.meta.env.BASE_URL}${Paths.SEQUENCE_REVIEW}/:launchId/:sequenceId`} element={<SequenceReviewPage />} />
      </Route>
      {/* Paths without sidebar */}
      <Route path={`${import.meta.env.BASE_URL}`} element={<App />}>
        <Route path={`${import.meta.env.BASE_URL}${Paths.FONT_UPLOADER}`} element={<FontUploader />} />
      </Route>
    </Routes>
  );
}


function AppRouter() {

  return (
    <React.Fragment>
      <BrowserRouter>
        <React.Suspense fallback={<Loader />}>
          <RouterWithNavigation />
        </React.Suspense>
      </BrowserRouter>
    </React.Fragment>
  )
}

class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error?: unknown }
> {
  state: { error?: unknown } = {};

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <pre style={{ padding: 24, whiteSpace: "pre-wrap" }}>
          {String(this.state.error)}
        </pre>
      );
    }
    return this.props.children;
  }
}

export default AppRouter;
export { RouteErrorBoundary };

